/**
 * Query client, toast host, error boundary.
 *
 * **Still no `WagmiProvider`, and that is the point.** The wallet stack is a
 * separate rollup chunk precisely so an anonymous visitor never downloads it
 * (`vite.config.ts`); mounting `WagmiProvider` here would pull it into the
 * critical path for every visitor, which is the opposite of what §5.1 asks for.
 *
 * Phase 5 adds `<WalletProvider>` instead, which is a different thing wearing a
 * similar name: it owns the identity modals and actions, renders from the
 * viem-free `Runtime.identityStore`, and `import()`s the wagmi config only when
 * the user shows intent to connect. The real wagmi client is constructed inside
 * that dynamic import and never at module scope.
 *
 * The query defaults are the ones docs/03 implies rather than TanStack's:
 *
 * - **`refetchOnWindowFocus: false`.** Every panel is "HTTP snapshot first, then
 *   WS deltas", so a tab regaining focus already has a socket that reconciles
 *   with `?since_ms=`. Refetching on focus would be a network request nobody
 *   asked for, and invariant 8 says the client has exactly two network timers.
 * - **`retry: 1`.** The API answers with RFC 9457 problem+json and the taxonomy
 *   in `api/http/problem.ts` distinguishes retryable from not; a blanket three
 *   retries would hammer a 400.
 * - **`staleTime` is per-query**, not global: `/config` is good for the session,
 *   a settled market is `immutable`, and `/markets/current` is stale the moment
 *   it lands.
 */

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastHost } from '@/components/primitives'
import { WalletProvider } from '@/components/wallet/WalletProvider'
import { ErrorBoundary } from './error-boundary'
import { initTheme } from './theme'
import { sharedRuntime, type Runtime } from './runtime'
import { RuntimeContext } from './use-runtime'

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
        staleTime: 0,
      },
    },
  })
}

const client = createQueryClient()

export function Providers({ children, runtime }: { children: ReactNode; runtime?: Runtime }) {
  // `main.tsx` applies the stored theme before the first paint; this keeps the
  // `system` choice following the OS for as long as the app is mounted.
  useEffect(() => initTheme(), [])

  // Created once, and never inside render. Two runtimes would be two sockets —
  // the one thing D8 forbids outright — and React 19 `StrictMode` mounts every
  // effect twice in development, which is exactly the shape of bug that finds.
  // `Runtime.start()` is idempotent for the same reason.
  const [resolved] = useState(() => runtime ?? sharedRuntime())

  return (
    <QueryClientProvider client={client}>
      <RuntimeContext.Provider value={resolved}>
        <ErrorBoundary>
          {/* `<WalletProvider>` is NOT `<WagmiProvider>`. It holds the modals and
              the actions, reads `Runtime.identityStore` (viem-free), and pulls
              the wallet chunk only when the user shows intent. Mounting the real
              wagmi provider here instead would put the signing stack on every
              anonymous visitor's critical path — see `wallet/lazy.ts`. */}
          <WalletProvider runtime={resolved}>
            {children}
            <ToastHost />
          </WalletProvider>
        </ErrorBoundary>
      </RuntimeContext.Provider>
    </QueryClientProvider>
  )
}
